import axios from 'axios';
import Cookies from 'js-cookie';
import { captureException } from '@sentry/react';

import { composeEmailValidator } from 'utils/fieldValidators';
import pushEvent, {
  EVENT_PRODUCT_DETAILS_CLICK_POST_REVIEW,
  EVENT_FEEDBACK_SUCCESS,
} from 'pushEvent/index';
import { getRequestVerificationToken } from 'store/common/settings/selectors';

import {
  getFeedbackChangeUsefulnessUrl,
  getFeedbackCookiePath,
  getFeedbackLoadMoreUrl,
  getFeedbackSubmitUrl,
  getFeedbackFetchCount,
  getFeedbackPageNumber,
  getFeedbackCount,
  getFeedbackFormData,
  getFeedbackPostsInitial,
  getFeedbackUserComment,
  getFeedbackUserEmail,
  getFeedbackUserName,
  getFeedbackUserRating,
  getProductId,
  getFeedbackNewRatedCookieName,
  getFeedbackNewRating,
  getFeedbackSortBy,
  getFeedbackCurrentCount,
} from '../selectors';
import {
  CHANGE_FEEDBACK_INPUT,
  SET_FEEDBACK_RATING,
  SUBMIT_FEEDBACK,
  SUBMIT_FEEDBACK_ERROR,
  SUBMIT_FEEDBACK_SUCCESS,
  RATE_FEEDBACK,
  USEFULLNESS_LIKE,
  USEFULLNESS_DISLIKE,
  FETCH_FEEDBACK_REQUEST,
  FETCH_FEEDBACK_SUCCESS,
  FETCH_FEEDBACK_ERROR,
} from './constants';
import {
  SET_FEEDBACK_LIST,
  SET_FEEDBACK_TOTAL_COUNT,
  SET_FEEDBACK_PAGE_NUMBER,
  SET_FEEDBACK_SORT_BY,
} from '../feedback/constants';
import {
  mapErrorResponseDetails,
  packNewRating,
  convertFeedbackPosts,
  getFetchedFeedbackCount,
} from './helpers';

/** set rating 1 to 5 stars */
export const setFeedbackRating = ({ value }) => ({
  type: SET_FEEDBACK_RATING,
  value,
});

/** only change field if value is not null */
export const changeFeedbackInput = ({ name = null, email = null, comment = null }) => ({
  type: CHANGE_FEEDBACK_INPUT,
  name,
  email,
  comment,
});

/** success callback for `submitFeedback` */
export const submitFeedbackSuccess = () => ({
  type: SUBMIT_FEEDBACK_SUCCESS,
});

/** error callback for `submitFeedback` */
export const submitFeedbackError = ({ details, message }) => ({
  type: SUBMIT_FEEDBACK_ERROR,
  details,
  message,
});

/**
 * post feedback, use submitFeedback as initial post interface to perform client-side validation
 * */
export const postFeedback = () => (dispatch, getState) => {
  const state = getState();
  const submitFeedbackUrl = getFeedbackSubmitUrl(state);
  const postData = {
    productRating: getFeedbackUserRating(state),
    userName: getFeedbackUserName(state),
    email: getFeedbackUserEmail(state),
    text: getFeedbackUserComment(state),
    shopProductId: getProductId(state),
  };

  dispatch({
    type: SUBMIT_FEEDBACK,
    data: postData,
  });

  const token = getRequestVerificationToken(state);

  axios({
    method: 'post',
    url: submitFeedbackUrl,
    data: postData,
    headers: {
      __RequestVerificationToken: token,
      'X-Requested-With': 'XMLHttpRequest',
    },
  })
    .then(({ data }) => {
      if (data.IsSucceeded) {
        dispatch(submitFeedbackSuccess());
      } else {
        dispatch(
          submitFeedbackError({
            details: mapErrorResponseDetails(data.Errors),
            message: data.ErrorMessage,
          }),
        );
      }
    })
    .catch((err) => {
      dispatch(submitFeedbackError({ details: {}, message: err }));
    });
};

export const submitFeedback = () => (dispatch, getState) => {
  /** first prevalidate data */
  const state = getState();
  const productId = getProductId(state);
  const { Rating, Name, Comment, Email } = getFeedbackFormData(state);
  const errors = {};

  if (!Rating) errors.Rating = 'Обязательное поле';
  if (!Name) errors.Name = 'Обязательное поле';
  if (!Comment) errors.Comment = 'Обязательное поле';
  if (!Email) {
    errors.Email = 'Обязательное поле';
  } else if (composeEmailValidator('Некорректный e-mail')(Email)) {
    errors.Email = 'Некорректный e-mail';
  }

  if (Object.keys(errors).length) {
    /** throw error */
    dispatch({
      type: SUBMIT_FEEDBACK_ERROR,
      details: errors,
    });
    return;
  }

  /** client validation succeeded, proceed submitting */
  dispatch(postFeedback());

  /** push to GTM */
  pushEvent(EVENT_FEEDBACK_SUCCESS, {
    rating: Rating,
  });
  /** push to YM */
  pushEvent(EVENT_PRODUCT_DETAILS_CLICK_POST_REVIEW, { productId });
};

export const updateFeedbacksCount = (receivedFeedbacksData) => (dispatch, getState) => {
  const state = getState();
  const initialCount = getFeedbackCount(state);
  const newCount = getFetchedFeedbackCount(initialCount, receivedFeedbacksData);

  if (initialCount !== newCount) {
    dispatch({
      type: SET_FEEDBACK_TOTAL_COUNT,
      count: newCount,
    });
  }
};

export const concatFeedbackPosts = (listFromStore, receivedFeedbacksData) => {
  const additionalList = convertFeedbackPosts(receivedFeedbacksData);
  return listFromStore.concat(additionalList);
};

/**
 * fetch feedbacks starting from page, with total of PagesCount according to perPage kept in state
 */
const fetchFeedbackPages =
  ({ Page, Count }, successCallback) =>
  (dispatch, getState) => {
    const state = getState();
    const feedbackLoadMoreUrl = getFeedbackLoadMoreUrl(state);
    const initialList = getFeedbackPostsInitial(state);
    const productId = getProductId(state);
    const sortBy = getFeedbackSortBy(state);
    /**
     * Fire request to show preloader
     */
    dispatch({
      type: FETCH_FEEDBACK_REQUEST,
    });

    axios({
      method: 'post',
      url: `${feedbackLoadMoreUrl}?mobile=false&jsonResult=true`,
      data: {
        ShopProductId: productId,
        PerPage: Count,
        SortType: sortBy,
        Page,
      },
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'content-type': 'application/json',
      },
    })
      .then((response) => {
        /**
         * concat and map received data
         */
        const concatedList = concatFeedbackPosts(initialList, response.data);
        /**
         * point succeeded fetch request
         */
        dispatch({
          type: FETCH_FEEDBACK_SUCCESS,
        });
        /**
         * update feedbacks list
         */
        dispatch({
          type: SET_FEEDBACK_LIST,
          list: concatedList,
        });

        /**
         * update feedbacks total count
         */
        dispatch(updateFeedbacksCount(response.data));
        if (successCallback) {
          successCallback();
        }
      })
      .catch((e) => {
        dispatch({
          type: FETCH_FEEDBACK_ERROR,
        });
        captureException(e);
      });
  };

/** Fetch feedback starting from Page, with PagesCount amount of pages */
export const fetchMoreFeedbacks = () => (dispatch, getState) => {
  const state = getState();
  const Page = getFeedbackPageNumber(state) + 1;
  const Count = getFeedbackFetchCount(state);

  return dispatch(
    fetchFeedbackPages({ Page, Count }, () => {
      dispatch({
        type: SET_FEEDBACK_PAGE_NUMBER,
        pageNumber: Page,
      });
    }),
  );
};

/** change sortBy filter, and do all subsequent actions, aka load new feedbacks */
export const setFeedbackSortBy =
  ({ value }) =>
  (dispatch, getState) => {
    const state = getState();
    const oldSortBy = getFeedbackSortBy(state);

    if (oldSortBy !== value) {
      // current last page - before count changed
      const Count = getFeedbackCurrentCount(state);
      /** change sort type and clean the list */
      dispatch({
        type: SET_FEEDBACK_SORT_BY,
        value,
      });

      /** and fetch new feedback items */
      dispatch(fetchFeedbackPages({ Page: 1, Count }));
    }
  };

/** post feedback */
export const rateFeedback =
  ({ feedbackId, isPositive = true }) =>
  (dispatch, getState) => {
    const state = getState();
    const changeUsefullnessUrl = getFeedbackChangeUsefulnessUrl(state);
    const newRatingCookie = getFeedbackNewRatedCookieName(state);
    const cookiePath = getFeedbackCookiePath(state);

    /**
     * Must Set cookie before dispatching redux event,
     * so that next selector cycle would get the updated value
     */
    const { liked, disliked } = getFeedbackNewRating(state);
    const updatedRatingData = packNewRating({
      liked: isPositive ? [...liked, feedbackId] : liked,
      disliked: !isPositive ? [...disliked, feedbackId] : disliked,
    });
    Cookies.set(newRatingCookie, updatedRatingData, { expires: 365, path: cookiePath });

    /**
     * gather data, dispatch redux event and send axios request
     */
    const postData = {
      feedbackId,
      usefullness: isPositive ? USEFULLNESS_LIKE : USEFULLNESS_DISLIKE,
      shopProductId: getProductId(state),
    };
    dispatch({
      type: RATE_FEEDBACK,
      data: postData,
      rand: Math.random(),
    });

    const token = getRequestVerificationToken(state);
    axios({
      method: 'post',
      url: changeUsefullnessUrl,
      data: postData,
      headers: {
        __RequestVerificationToken: token,
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
  };

export const onFeedbackMount = () => (dispatch, getState) => {
  const state = getState();
  const value = getFeedbackSortBy(state);

  const Count = getFeedbackCurrentCount(state);
  /** change sort type and clean the list */
  dispatch({
    type: SET_FEEDBACK_SORT_BY,
    value,
  });
  dispatch(fetchFeedbackPages({ Page: 1, Count }));
};
